import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useRange } from '../../range';
import { Band, Card } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import KpiTile from '../../components/KpiTile';
import PageHeader from '../../components/PageHeader';
import Sparkline from '../../components/Sparkline';
import StatusPill from '../../components/StatusPill';
import { formatCompactNumber, formatPct, formatUsd, humanize } from '../../format';

/**
 * System › Health & cost.
 *
 * The product doc is direct about what this page must not become: "This is
 * native CloudWatch, surfaced through the panel, not rebuilt ... a chart is
 * not a competitive advantage." So this page links out for infrastructure
 * health and the AWS bill, and only renders the two costs those tools cannot
 * see — model spend and try-on volume, both of which are facts about our own
 * data rather than AWS metrics.
 *
 * The "not measured here" list is deliberate and load-bearing. An admin who
 * believes this page's health badge covers infrastructure will stop checking
 * the place it is actually measured, which is a worse outcome than having no
 * page at all.
 */
function LinkCard({ link }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noreferrer noopener"
      className="group block rounded-xl border border-hairline bg-surface px-4 py-3 shadow-sm transition-colors hover:border-baseline hover:bg-surface-page"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">{link.label}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0 text-ink-muted" aria-hidden="true">
          <path d="M4 2.5h5.5V8M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <p className="mt-0.5 text-xs leading-snug text-ink-muted">{link.detail}</p>
    </a>
  );
}

export default function HealthPage() {
  const range = useRange();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/system/health', { params: { days: range.days } });
      setData(data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load system health'));
    } finally {
      setLoading(false);
    }
  }, [range.days]);

  useEffect(() => { load(); }, [load]);

  const spend = data?.model_spend;
  const tryon = data?.tryon_volume;
  const crawl = data?.crawl;
  const spendTrend = (spend?.trend || []).map((d) => d.cost_usd);

  return (
    <div>
      <PageHeader
        title="Health & cost"
        hint={`What MAHI itself is costing — ${range.label.toLowerCase()}. Infrastructure health and the AWS bill live in CloudWatch and Cost Explorer.`}
      />

      {loading && !data ? (
        <Card><p className="text-sm text-ink-muted">Loading…</p></Card>
      ) : (
        <>
          <Band title="Service">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Card>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink-secondary">Admin API</span>
                  <StatusPill tone="good" label="Responding" />
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  This page loaded, so the service is up. Real uptime is in CloudWatch.
                </p>
              </Card>
              <Card>
                <div className="text-sm font-medium text-ink-secondary">Region</div>
                <div className="mt-1 font-mono text-sm text-ink">{data?.region}</div>
              </Card>
              <Card>
                <div className="text-sm font-medium text-ink-secondary">Table prefix</div>
                <div className="mt-1 font-mono text-sm text-ink">{data?.table_prefix}</div>
              </Card>
            </div>
          </Band>

          <Band title="Model spend">
            {spend?.available ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <KpiTile
                    label="Stylist spend"
                    value={formatUsd(spend.cost_usd)}
                    goodWhen="down"
                    trend={spendTrend.length > 1 ? spendTrend : null}
                    caption={spend.partial
                      ? `Only ${spend.days_priced} of ${spend.days_with_data} days had pricing configured — this is not the full window.`
                      : `Across ${spend.days_with_data} days`}
                  />
                  <KpiTile label="Per conversation" value={formatUsd(spend.cost_per_conversation_usd)} goodWhen="down" />
                  <KpiTile label="Per turn" value={formatUsd(spend.cost_per_turn_usd)} goodWhen="down" />
                  <KpiTile
                    label="Cached input tokens"
                    value={formatCompactNumber(spend.cache_read_tokens)}
                    caption="Billed at the cheaper cache-read rate"
                  />
                </div>
                {spendTrend.length > 1 && (
                  <div className="mt-4">
                    <Card title="Daily spend">
                      <Sparkline values={spendTrend} height={48} color="#eb6834" />
                    </Card>
                  </div>
                )}
              </>
            ) : (
              <EmptyState
                tone="gap"
                title="Model spend is not available"
                detail={spend?.reason || 'No stylist rollup data in this window.'}
              />
            )}
          </Band>

          <Band title="Try-on volume">
            {tryon?.available ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiTile label="Requested" value={formatCompactNumber(tryon.requested)}
                         caption="Every completed try-on is a paid call to the external engine." />
                <KpiTile label="Completed" value={formatCompactNumber(tryon.completed)} />
                <KpiTile label="Failed" value={formatCompactNumber(tryon.failed)} goodWhen="down" />
                <KpiTile label="Success rate" value={formatPct(tryon.success_rate_pct, 1)} />
              </div>
            ) : (
              <EmptyState title="No try-on data in this window" />
            )}
          </Band>

          <Band title="Crawl reliability">
            <Card>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                <div>
                  <div className="text-xs text-ink-muted">Jobs</div>
                  <div className="text-xl font-semibold tabular-nums text-ink">{crawl?.total ?? '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-muted">Success rate</div>
                  <div className="text-xl font-semibold tabular-nums text-ink">{formatPct(crawl?.success_rate_pct, 1)}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-muted">In flight</div>
                  <div className="text-xl font-semibold tabular-nums text-ink">{crawl?.in_flight ?? '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-muted">Breakdown</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(crawl?.by_status || {}).map(([status, count]) => (
                      <StatusPill key={status} status={status} label={`${humanize(status)} ${count}`} />
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </Band>

          <Band title="Measured elsewhere">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(data?.links || []).map((link) => <LinkCard key={link.key} link={link} />)}
            </div>
            <ul className="mt-4 space-y-1.5">
              {(data?.not_measured_here || []).map((line) => (
                <li key={line} className="flex items-start gap-2 text-xs text-ink-secondary">
                  <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-baseline" aria-hidden="true" />
                  {line}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] leading-relaxed text-ink-muted">
              These are not rebuilt here on purpose. CloudWatch and Cost Explorer already do them well, and a
              second copy that drifts is worse than a link — particularly for numbers someone would act on
              during an incident.
            </p>
          </Band>
        </>
      )}
    </div>
  );
}
